import { Controller } from "@hotwired/stimulus";
import { loadPreferences } from "./user_preferences_controller";
import { prefersReducedMotion } from "../helpers";

/**
 * Tabs Controller
 *
 * Usage:
 *   1. Add an id to the container element
 *      e.g., <article id="control" data-controller="tabs">
 *   2. Add these attributes to tab links:
 *      - data-tabs-target="tabItem"
 *      - data-action="click->tabs#showTab"
 *      - data-tabs-controls-param="anchor_name"
 *   3. Add these attributes to tab contents:
 *      - id="anchor_name"
 *      - data-tabs-target="tabPanel"
 *
 * Options:
 *   - data-tabs-update-url-value="false" (don't update URL)
 *   - data-tabs-all-hidden-value="true" (all tabs hidden initially)
 *   - data-user-preferences="tabs:currentTab" (save the current tab via user_preferences_controller)
 *   - data-tabs-default-tab-value="tab1"
 *
 * Markup Example:
 *
 * <article id="control" data-controller="tabs">
 *   <nav>
 *     <a data-tabs-target="tabItem" data-action="click->tabs#showTab" data-tabs-controls-param="tab1">Tab 1</a>
 *     <a data-tabs-target="tabItem" data-action="click->tabs#showTab" data-tabs-controls-param="tab2">Tab 2</a>
 *   </nav>
 *
 *   <section>
 *     <div id="tab1" data-tabs-target="tabPanel">Content 1</div>
 *     <div id="tab2" data-tabs-target="tabPanel">Content 2</div>
 *   </section>
 * </article>
 */

export default class extends Controller {
  static targets = ["tabItem", "tabPanel", "scopedLink"];
  static values = {
    currentTab: String,
    defaultTab: String,
    updateUrl: {
      type: Boolean,
      default: true,
    },
    allHidden: {
      type: Boolean,
      default: false,
    },
    activeClass: { type: String, default: "active" },
    inactiveClass: { type: String, default: "inactive" },
  };

  initialize() {
    loadPreferences(this);
  }

  connect() {
    // Show a warning when HTMLElement has no id tag
    if (this.updateUrlValue && !this.element.id) {
      return console.warn(
        "Add an id to this HTMLElement in order to update the URL",
        this.element
      );
    }

    // a11y
    this.tabItemTargets.forEach((tab) => {
      tab.role = "tab";
      tab.setAttribute("aria-controls", tab.dataset.tabsControlsParam);
    });
    this.tabPanelTargets.forEach((tab) => (tab.role = "tabpanel"));
    this._markTabList();

    // Screen readers announce these as tabs, so they have to answer to the
    // arrow keys a tab list is expected to answer to.
    this.boundHandleTabKeydown = this._handleTabKeydown.bind(this)
    this.element.addEventListener("keydown", this.boundHandleTabKeydown)

    this._initTabs();

    // Listen for sidebar-tabs:activate event
    this.boundHandleTabActivation = this._handleTabActivation.bind(this)
    window.addEventListener('sidebar-tabs:activate', this.boundHandleTabActivation)

    // Mark lazy frames as loaded once their content arrives so re-activating
    // the tab never reloads (and wipes) them.
    this.boundMarkFrameLoaded = this._markFrameLoaded.bind(this)
    this.element.addEventListener('turbo:frame-load', this.boundMarkFrameLoaded)

    // Listen to back button to handle it better
    window.addEventListener("popstate", this._handlePopState.bind(this))
  }

  disconnect() {
    window.removeEventListener('sidebar-tabs:activate', this.boundHandleTabActivation)
    this.element.removeEventListener('turbo:frame-load', this.boundMarkFrameLoaded)
    this.element.removeEventListener("keydown", this.boundHandleTabKeydown)
    window.removeEventListener("popstate", this._handlePopState.bind(this))
  }

  // `role="tablist"` is only valid when the tabs really are its children. Some
  // call sites wrap each tab in its own heading, and claiming a tab list there
  // would describe a structure that doesn't exist.
  _markTabList() {
    const tabs = this.tabItemTargets
    if (tabs.length === 0) return

    const parent = tabs[0].parentElement
    if (!parent) return
    if (!tabs.every((tab) => tab.parentElement === parent)) return
    if (parent.getAttribute("role") === "tablist") return

    parent.setAttribute("role", "tablist")
  }

  _handleTabKeydown(event) {
    const tabs = this.tabItemTargets
    if (tabs.length === 0) return

    const currentIndex = tabs.indexOf(event.target)
    if (currentIndex === -1) return

    let targetIndex
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        targetIndex = (currentIndex + 1) % tabs.length
        break
      case "ArrowLeft":
      case "ArrowUp":
        targetIndex = (currentIndex - 1 + tabs.length) % tabs.length
        break
      case "Home":
        targetIndex = 0
        break
      case "End":
        targetIndex = tabs.length - 1
        break
      default:
        return
    }

    event.preventDefault()

    const tab = tabs[targetIndex]
    tab.focus()
    // Clicking rather than calling showTab directly: it goes through the exact
    // same path as a pointer user, so the two can't drift apart. Selection
    // follows focus, which is the expected tab-list behaviour and is safe here
    // because every panel is inline content, not a fresh page load.
    tab.click()
  }

  showTab(event = {}, activeTab = "") {
    const type = event.type;
    const params = event.params || {};

    if (type === "click") {
      event.preventDefault();
    }

    this.currentTabValue = type === "click" ? params.controls : activeTab;

    if (this.allHiddenValue) {
      // Toggle the active panel visibility
      this.tabPanelTargets.forEach((element) => {
        if (element.id === this.currentTabValue) {
          element.hidden = !element.hidden;
        } else {
          element.hidden = true;
        }
      });
    } else {
      // Toggle the active panel
      this.tabPanelTargets.forEach((element) => {
        element.hidden = element.id !== this.currentTabValue;
      });
    }

    // Trigger lazy turbo-frames inside the active panel.
    //
    // This kicks the *initial* load of a lazy frame when its panel becomes
    // visible (toggling `hidden` doesn't reliably fire Turbo's lazy loader).
    // We must NOT re-trigger a frame that has already loaded its content: a
    // frame can transiently report `complete === false` while it is mid-flight,
    // and reloading it replaces its content, wiping any in-progress UI inside
    // (e.g. unsaved comment forms).
    const activePanel = this.tabPanelTargets.find(p => p.id === this.currentTabValue)
    if (activePanel) {
      activePanel.querySelectorAll('turbo-frame[loading="lazy"][src]').forEach(frame => {
        if (frame.dataset.lazyLoaded === "true") return
        if (!frame.complete) frame.src = frame.src
      })

      // Fade the incoming panel in only on a real user switch. Programmatic
      // calls (init, popstate, sidebar activation) must not animate, or every
      // panel visible on first paint would flicker in on page load.
      if (type === "click") this._playReveal(activePanel)
    }

    // Toggle the active tab
    this.tabItemTargets.forEach((tab) =>
      tab.setAttribute(
        "aria-selected",
        tab.dataset.tabsControlsParam === this.currentTabValue
      )
    );

    if (this.updateUrlValue) {
      this._setUpdateUrl();
    }

    this._syncScopedLinks();

    if (document.getElementById(`${this.element.id}_tab`)) {
      document.getElementById(`${this.element.id}_tab`).value =
        this.currentTabValue;
    }
  }

  // Keep in-page navigation links (e.g. the Míos|Organización toggle, the
  // clear-filters link) pointing at the tab the user is currently viewing.
  // The active tab is client-side state, so a server-rendered href can't know
  // it (or hardcodes a stale default). Syncing the `${element.id}_tab` param on
  // these links makes the URL the source of truth, so the view survives the
  // full navigation without relying on the localStorage fallback.
  scopedLinkTargetConnected(link) {
    this._applyTabToLink(link);
  }

  _syncScopedLinks() {
    this.scopedLinkTargets.forEach((link) => this._applyTabToLink(link));
  }

  _applyTabToLink(link) {
    if (!this.currentTabValue) return;

    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set(`${this.element.id}_tab`, this.currentTabValue);
      link.href = url.toString();
    } catch {
      // Ignore links with a malformed href; there's nothing to preserve.
    }
  }

  // Private methods

  // Fade a panel in via the shared `reveal-in` keyframe. Skipped entirely under
  // prefers-reduced-motion (the CSS override only collapses the duration, but
  // there's no reason to run it at all here).
  _playReveal(element) {
    if (prefersReducedMotion()) return

    element.style.animation = "reveal-in 180ms ease-out"
    element.addEventListener("animationend", () => { element.style.animation = "" }, { once: true })
  }

  _markFrameLoaded(event) {
    const frame = event.target
    if (frame.tagName === "TURBO-FRAME") {
      frame.dataset.lazyLoaded = "true"
    }
  }

  _handleTabActivation(event) {
    const tab = event.detail.tab
    // The 'sidebar-tabs:activate' event is dispatched at window-level, so
    // every tabs controller on the page receives it. Ignore it unless this
    // controller actually owns a tab with the requested name; otherwise an
    // unrelated tab group (e.g. /redactor's all/grouped/kanban list) would
    // hide all its panels and end up with no active tab.
    const hasTab = this.tabItemTargets.some(
      (item) => item.dataset.tabsControlsParam === tab
    )
    if (!hasTab) return

    this.showTab({}, tab)
  }

  _setUpdateUrl() {
    const queryParams = new URLSearchParams(window.location.search);
    const oldTab = queryParams.get(`${this.element.id}_tab`);
    queryParams.set(`${this.element.id}_tab`, this.currentTabValue);

    // Only create a new entry in history if it's a different tab
    if (oldTab !== this.currentTabValue) {
      // Preserve hash if it exists
      const hash = window.location.hash || ''
      const newUrl = `${window.location.pathname}?${queryParams.toString()}${hash}`;

      if (oldTab) {
        // Add new entry to history
        this._updateHistory(newUrl)

        // Dispatch event for Turbo to know about the change
        const event = new CustomEvent('turbo:load', {
          detail: { url: newUrl }
        });
        window.dispatchEvent(event);
      } else {
        // Replace new entry to history
        this._updateHistory(newUrl, true)
      }
    }
  }

  _updateHistory(newUrl, replace = false) {
    const history = window.history;
    const state = {
      turbo: {},
      tab: this.currentTabValue,
      url: newUrl
    };

    if (replace) {
      history.replaceState(state, '', newUrl);
    } else {
      history.pushState(state, '', newUrl);
    }
  }

  _showTabSelected(hash) {
    const tab = this.tabItemTargets.find((target) =>
      target.dataset.tabsControlsParam.includes(hash)
    );
    if (tab) {
      this.showTab({}, hash);
    }
  }

  _initTabs() {
    // if the URL contains a hash
    const activeTab = new URL(window.location.href).searchParams.get(
      `${this.element.id}_tab`
    );
    if (activeTab) {
      return this._showTabSelected(activeTab);
    }

    // if the tab is explicitly set by the arguments (values)
    if (this.hasCurrentTabValue) {
      return this._showTabSelected(this.currentTabValue);
    }

    // if the tab is specified by data-tabs-default-tab-value
    const defaultTab = this.element.dataset.tabsDefaultTabValue;
    if (defaultTab) {
      return this._showTabSelected(defaultTab);
    }

    // If there are no tabs, do nothing
    if(this.tabPanelTargets.length == 0) {
      return
    }

    // otherwise, the first tab is shown.
    return this.showTab({}, this.tabPanelTargets[0].id);
  }

  _handlePopState(event) {
    const queryParams = new URLSearchParams(window.location.search);
    const tabFromUrl = queryParams.get(`${this.element.id}_tab`);

    if (tabFromUrl) {
      this._showTabSelected(tabFromUrl);
    }
  }

  // Activate a tab by clicking on it
  activate(event) {
    event.preventDefault()
    this.activateTab(event.currentTarget)
  }

  // Activate a tab by its parameter
  activateTabByParam(param) {
    const tabItem = this.tabItemTargets.find(item => item.dataset.tabsControlsParam === param)
    if (tabItem) {
      this.activateTab(tabItem)
    }
  }

  // Activate a tab
  activateTab(tabItem) {
    // Get the tab panel to activate
    const tabPanel = this.tabPanelTargets.find(panel => panel.id === tabItem.dataset.tabsControlsParam)

    if (!tabPanel) {
      return
    }

    // Deactivate all tabs
    this.tabItemTargets.forEach(item => {
      item.classList.remove(this.activeClassValue)
      item.classList.add(this.inactiveClassValue)
      item.setAttribute("aria-selected", "false")
    })

    // Deactivate all panels
    this.tabPanelTargets.forEach(panel => {
      panel.classList.remove(this.activeClassValue)
      panel.classList.add(this.inactiveClassValue)
      panel.setAttribute("aria-hidden", "true")
    })

    // Activate the selected tab
    tabItem.classList.add(this.activeClassValue)
    tabItem.classList.remove(this.inactiveClassValue)
    tabItem.setAttribute("aria-selected", "true")

    // Activate the selected panel
    tabPanel.classList.add(this.activeClassValue)
    tabPanel.classList.remove(this.inactiveClassValue)
    tabPanel.setAttribute("aria-hidden", "false")

    // Dispatch a custom event
    this.element.dispatchEvent(
      new CustomEvent("tabs:change", {
        detail: {
          tabId: tabPanel.id,
          tabItem: tabItem,
          tabPanel: tabPanel
        }
      })
    )
  }
}
